import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles, FormControl, InputLabel, Select, MenuItem, Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination } from '@material-ui/core'
import AdminMainLayout from './AdminMainLayout'
import { AdminCurrencyFormat } from './AdminCurrencyFormat'
import { adminSalesList, adminTotalSalesList, adminSalesEventBreakdownList } from '../actions/admin_sales'
import { countryList } from '../actions/admin_countries'
import { updateListPagination } from '../../orm/orm_list_actions'
import { get } from 'lodash'

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
    width: '100%',
    justifyContent: 'flex-start',
  },
  formControl: {
    minWidth: 120,
  },
  totalsContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  totalBox: {
    padding: '12px 20px',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  totalLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '4px',
  },
  totalValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  selectedRow: {
    backgroundColor: '#e3f2fd !important',
    '&:hover': {
      backgroundColor: '#bbdefb !important',
    },
  },
}))

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

const currentYear = new Date().getFullYear()
const YEARS = []
for (let year = currentYear; year >= 2020; year--) {
  YEARS.push(year)
}

export const AdminSales = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const is_loading = useSelector(() => adminSalesList.isLoading())
  const countries = useSelector(() => countryList.getVisibleObjects())
  const salesTotals = useSelector(() => adminTotalSalesList.getVisibleObjects())[0]
  const totalPhotographerAmount = salesTotals?.total_photographer_amount ?? 0
  const totalRevenue = salesTotals?.total_revenue ?? 0

  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1)
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [selectedPhotographerId, setSelectedPhotographerId] = useState(null)
  const salesRows = useSelector(() => adminSalesList.getVisibleObjects())
  const eventBreakdownRows = useSelector(() => adminSalesEventBreakdownList.getVisibleObjects())

  // Sort countries alphabetically by name
  const sortedCountries = [...countries].sort((a, b) =>
    (a.country_name || '').localeCompare(b.country_name || '')
  )

  // Set Australia as default when countries load
  useEffect(() => {
    if (countries.length > 0 && selectedCountry === null) {
      const australia = countries.find(c => c.country_name === 'Australia')
      if (australia) {
        setSelectedCountry(australia.id)
      } else if (sortedCountries.length > 0) {
        setSelectedCountry(sortedCountries[0].id)
      }
    }
  }, [countries])

  useEffect(() => {
    dispatch(countryList.fetchListIfNeeded())
  }, [])

  useEffect(() => {
    const filter = { year: selectedYear, month: selectedMonth }
    if (selectedCountry) {
      filter.country_id = selectedCountry
    }
    dispatch(adminSalesList.updateListFilter(filter))
    dispatch(adminSalesList.fetchListIfNeeded())
    dispatch(adminTotalSalesList.updateListFilter(filter))
    dispatch(adminTotalSalesList.fetchListIfNeeded())
  }, [selectedYear, selectedMonth, selectedCountry])

  useEffect(() => {
    if (selectedPhotographerId) {
      const filter = {
        photographer_id: selectedPhotographerId,
        year: selectedYear,
        month: selectedMonth
      }
      if (selectedCountry) {
        filter.country_id = selectedCountry
      }
      dispatch(adminSalesEventBreakdownList.updateListFilter(filter))
      dispatch(adminSalesEventBreakdownList.invalidateList())
      dispatch(adminSalesEventBreakdownList.fetchListIfNeeded())
    }
  }, [selectedPhotographerId, selectedYear, selectedMonth, selectedCountry])

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value)
  }

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value)
  }

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value)
  }

  const handleRowClick = (photographerId) => {
    if (selectedPhotographerId === photographerId) {
      setSelectedPhotographerId(null)
    } else {
      setSelectedPhotographerId(photographerId)
    }
  }

  const handleChangePage = (event, newPage) => {
    dispatch(updateListPagination(adminSalesList.listKey, { page: newPage + 1 }))
    dispatch(adminSalesList.invalidateList())
    dispatch(adminSalesList.fetchListIfNeeded())
  }

  const handleChangeRowsPerPage = (event) => {
    dispatch(updateListPagination(adminSalesList.listKey, { items_per_page: parseInt(event.target.value, 10), page: 1 }))
    dispatch(adminSalesList.invalidateList())
    dispatch(adminSalesList.fetchListIfNeeded())
  }

  const onRefresh = () => {
    dispatch(adminSalesList.invalidateList())
    dispatch(adminSalesList.fetchListIfNeeded())
  }

  const renderFilters = () => {
    return (
      <div>
        <div className={classes.filterContainer}>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              label="Year"
            >
              {YEARS.map((year) => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              label="Month"
            >
              {MONTHS.map((month) => (
                <MenuItem key={month.value} value={month.value}>{month.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Country</InputLabel>
            <Select
              value={selectedCountry || ''}
              onChange={handleCountryChange}
              label="Country"
            >
              {sortedCountries.map((country) => (
                <MenuItem key={country.id} value={country.id}>{country.country_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={classes.totalsContainer}>
          <div className={classes.totalBox}>
            <div className={classes.totalLabel}>Total Photographer Amount</div>
            <div className={classes.totalValue}><AdminCurrencyFormat value={ totalPhotographerAmount } /></div>
          </div>
          <div className={classes.totalBox}>
            <div className={classes.totalLabel}>Total Revenue</div>
            <div className={classes.totalValue}><AdminCurrencyFormat value={ totalRevenue } /></div>
          </div>
        </div>
      </div>
)
}

  const selectedMonthName = MONTHS.find(m => m.value === selectedMonth)?.label || ''

  return (
    <AdminMainLayout
      active_key='sales'
      title={`Sales - ${selectedMonthName} ${selectedYear}`}
      breadcrumbs={[
        { name: 'admin_home' },
        {
          name: 'sales',
          label: 'Sales',
          url: '/admin/sales',
        },
      ]}
    >
      <Box display="flex" justifyContent="flex-end">
        {renderFilters()}
      </Box>
      <Paper variant="outlined" square>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Photographer ID</TableCell>
                <TableCell>First Name</TableCell>
                {selectedPhotographerId ? (
                  <>
                    <TableCell>Event</TableCell>
                    <TableCell>Album</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Name Used</TableCell>
                  </>
                )}
                <TableCell align="right">Photographer Amount</TableCell>
                <TableCell align="right">Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedPhotographerId ? (
                <>
                  <TableRow
                    onClick={() => setSelectedPhotographerId(null)}
                    className={classes.tableRow}
                    style={{ backgroundColor: '#e3f2fd' }}
                  >
                    <TableCell>{selectedPhotographerId}</TableCell>
                    <TableCell>{salesRows.find(r => r.id === selectedPhotographerId)?.first_name}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                    <TableCell align="right">
                      <AdminCurrencyFormat value={salesRows.find(r => r.id === selectedPhotographerId)?.photographer_amount} />
                    </TableCell>
                    <TableCell align="right">
                      <AdminCurrencyFormat value={salesRows.find(r => r.id === selectedPhotographerId)?.revenue} />
                    </TableCell>
                  </TableRow>
                  {eventBreakdownRows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell>{row.event_name}</TableCell>
                      <TableCell></TableCell>
                      <TableCell align="right"><AdminCurrencyFormat value={row.photographer_amount} /></TableCell>
                      <TableCell align="right"><AdminCurrencyFormat value={row.revenue} /></TableCell>
                    </TableRow>
                  ))}
                  {eventBreakdownRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>No event data</TableCell>
                    </TableRow>
                  )}
                </>
              ) : (
                <>
                  {salesRows.map((row) => (
                    <TableRow
                      key={row.id}
                      onClick={() => handleRowClick(row.id)}
                      className={classes.tableRow}
                    >
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.first_name}</TableCell>
                      <TableCell>{row.last_name}</TableCell>
                      <TableCell>{row.name_used}</TableCell>
                      <TableCell align="right"><AdminCurrencyFormat value={row.photographer_amount} /></TableCell>
                      <TableCell align="right"><AdminCurrencyFormat value={row.revenue} /></TableCell>
                    </TableRow>
                  ))}
                  {salesRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>No rows</TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display="flex" alignItems="center">
          <Box style={{ cursor: 'pointer', padding: '8px 16px' }} onClick={onRefresh}>REFRESH</Box>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={get(adminSalesList.getPagination(), 'num_items', 0)}
            rowsPerPage={get(adminSalesList.getPagination(), 'items_per_page', 10)}
            page={get(adminSalesList.getPagination(), 'current_page', 1) - 1}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
    </AdminMainLayout>
  )
}
